import React, { useState } from "react";
import axios from "axios";
import FormDialog from "components/modal";
import { baseURL } from "services/API";
import { Box, Card, CardContent, Typography, Grid, FormControl, InputLabel, Select, MenuItem, Button, Snackbar, Alert} from "@mui/material";
import LandlordDetailsTable from "components/Tables/LandlordDetailsTable";
import AddPaymentForm from "components/Forms/LandlordPaymentForm";
import { FlexBetween, Flex } from "components/FlexBox/FlexBox";
import BasicCard from "components/Card";
import { withoutSymbolFormatter } from "utilities/currencyFormatter";
import { useLandlordPayments } from "services/hooks/rentals";
import { useSelector } from "react-redux";
import * as yup from 'yup';
import moment from "moment";
import EditLandlorPayments from "components/LandlordPaymentsModal/EditLandlordPayments";
import { parseApprovalResponse, apiErrorMessage } from "utilities/approvalResponse";
import TopSnack from "components/TopSnack";

const LandlordPaymentDetails = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const currentMonth = moment().month() + 1; // moment().month() returns 0-11, so we add 1
  const currentYear = moment().year();
  const [filterMonth, setFilterMonth] = useState(currentMonth.toString());
  const [filterYear, setFilterYear] = useState(currentYear.toString());
  const [openDialog, setOpenDialog] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [severity, setSeverity] = React.useState("");
  const [message, setMessage] = React.useState("");
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [newPayment, setNewPayment] = useState({
    related_landlord_rental: undefined, // Ensure this field is included in the state
    amount: 0,
    description: "",
    date_paid: moment().format('YYYY-MM-DD'),
    related_month: "",
    paid_by: undefined,
  });
  const [selectedLandlordPayment, setSelectedLandlordPayment] = React.useState(null);
  
  const [openSnack, setOpenSnack] = React.useState({
    open: false,
    vertical: "top",
    horizontal: "center",
  });
  const [errors, setErrors] = useState({});
  const [isSubmittingData, setIsSubmittingData] = useState(false);
  const [monthsAndYears, setMonthsAndYears] = useState([]);
  const [openSnackbar, setOpenSnackbar] = useState(false); // State for Snackbar
  const landlord = useSelector((state) => state.landlord); 
  const { landlordPayments, isLoadingLandlordPayments, errorLandlordPayments, setNewItemData } = useLandlordPayments(landlord.id, filterYear, filterMonth);

  // Calculate adjusted rental payments and balance
  const adjustedRentalPayments = parseFloat(landlordPayments.total_rental_payments) * (landlordPayments.pay_percentage / 100);
  const balance = parseFloat(landlordPayments.cumulative_landlord_balance);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterMonthChange = (event) => {
    setFilterMonth(event.target.value);
  };

  const handleFilterYearChange = (event) => {
    setFilterYear(event.target.value);
  };

  const handleMenuClick = (event, landlordPayment) => {
    setAnchorEl(event.currentTarget);
    setSelectedLandlordPayment(landlordPayment);
  };

  const handleMenuClose = () => {
    setSelectedLandlordPayment(null);
    setAnchorEl(null);
  };

  const handleOpenDialog = () => {
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleEditDialogClose = () => {
    setEditDialogOpen(false);
  };

  const handleEditDialogOpen = () => {
    const rental = selectedLandlordPayment.related_landlord_rental;
    setSelectedLandlordPayment({
      id: selectedLandlordPayment.id,
      related_landlord_rental: rental?.id ?? null,
      amount: selectedLandlordPayment.amount,
      description: selectedLandlordPayment.description ?? "",
      date_paid: moment(selectedLandlordPayment.date_paid).format("YYYY-MM-DD"),
      related_month: selectedLandlordPayment.related_month?.id ?? ""
    });
    setEditDialogOpen(true);
    setAnchorEl(null);
  };
  const handleDeleteDialogClose = () => {
    setDeleteDialogOpen(false);
  };
  const handleDeleteDialogOpen = () => {
    setDeleteDialogOpen(true);
    setAnchorEl(null);
  };

  const handleDeletePayment = async () => {
    try {
      const response = await axios.delete(`${baseURL}/admin/landlord_payments/details/${selectedLandlordPayment.id}`);

      setOpenSnack({ open: true, vertical: "top", horizontal: "center" });
      setSeverity("success");
      setMessage("Payment deleted successfully");
      setNewItemData();
      setAnchorEl(null);
    } catch (error) {
      console.error("Error deleting payment:", error);

      setOpenSnack({ open: true, vertical: "top", horizontal: "center" });
      setSeverity("error");
      setMessage("Error deleting payment");
    }
    setDeleteDialogOpen(false);
  }

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    
    // Handle "All Properties" option - set to null
    if (name === "related_landlord_rental" && value === "all") {
      setNewPayment((prev) => ({...prev, [name]: null}));
      setErrors((prev) => ({ ...prev, [name]: "" }));
      return;
    }
    
    setNewPayment((prev) => ({...prev, [name]: value,}));
    if (!toString(value).trim()) {
      setErrors((prev) => ({ ...prev, [name]: `${name} is required` }));
    } else {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };



  const handleEditInputChange = (event) => {
    const { name, value } = event.target;
    if (name === "related_landlord_rental") {
      const next = value === "all" ? null : (value === "" ? null : Number(value));
      setSelectedLandlordPayment((prev) => ({ ...prev, [name]: next }));
      setErrors((prev) => ({ ...prev, [name]: "" }));
      return;
    }
    setSelectedLandlordPayment((prev) => ({ ...prev, [name]: value }));
    if (!toString(value).trim()) {
      setErrors((prev) => ({ ...prev, [name]: `${name} is required` }));
    } else {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleMonthChange = (event) => {
    const selectedMonth = event.target.value;
    const selectedMonthId =
      monthsAndYears.find(
        (month) => `${month.month} ${month.year}` === selectedMonth
      )?.id || "";
    setNewPayment((prev) => ({
      ...prev,
      related_month: selectedMonthId,
    }));
  };


  const paymentSchema = yup.object().shape({
		related_landlord_rental: yup.number().nullable().test(
      'is-valid-property',
      'Please select a property or All Properties',
      function(value) {
        // Allow null (All Properties) or a valid number (specific property)
        return value === null || (typeof value === 'number' && !isNaN(value));
      }
    ),
		amount: yup.number().min(1, 'The amount must be greater than zero').required("Input the payment amount"),
		date_paid: yup.date().required("Select date for yhe payment"),
    related_month: yup.string().required("Please enter the month for the payement"),
		description: yup.string().required("This is required"),
		paid_by: yup.number().required("This is required"),
	});

  const editPaymentSchema = yup.object().shape({
		related_landlord_rental: yup.number().nullable().test(
      "is-valid-property",
      "Please select a property or All Properties",
      (value) => value === null || (typeof value === "number" && !isNaN(value))
    ),
		amount: yup.number().min(1, "The amount must be greater than zero").required("Input the payment amount"),
		date_paid: yup.date().required("Select date for the payment"),
    related_month: yup.string().required("Please enter the month for the payment"),
		description: yup.string().required("This is required"),
	});

  const handleAddPayment = async () => {

    try {
			await paymentSchema.validate(newPayment, { abortEarly: false });
		} catch (error) {
			const newError = {}
			error.inner.forEach(err => {
				newError[err.path]=err.message
			})
			setErrors(newError)
			return
		}

    // Prepare payment data - include related_landlord when related_landlord_rental is null
    const paymentData = {
      ...newPayment,
      ...(newPayment.related_landlord_rental === null && { related_landlord: landlord.id })
    };

    setIsSubmittingData(true);
    try {
      const response = await axios.post(`${baseURL}/admin/landlord_payments`, paymentData);
      const approval = parseApprovalResponse(response);
      if (approval.pendingApproval) {
        setSeverity("info");
        setMessage(
          approval.message ||
          `Submitted for approval (request #${approval.approvalRequestId}). View it in Audit & Approvals.`
        );
        setOpenSnack({ open: true, vertical: "top", horizontal: "center" });
        handleCloseDialog();
      } else {
        setOpenSnackbar(true);
        handleCloseDialog();
      }
    } catch (error) {
      console.error("There was an error adding the payment!", error);
      setSeverity("error");
      setMessage(apiErrorMessage(error, "Failed to add payment."));
      setOpenSnack({ open: true, vertical: "top", horizontal: "center" });
    } finally {
      setIsSubmittingData(false);
      setNewItemData(true);
    }
  };

  const handleEditLandlordPayments = async () => {
    try {
      await editPaymentSchema.validate(selectedLandlordPayment, {
        abortEarly: false,
      });
    } catch (error) {
      const newError = {};
      error.inner.forEach((err) => {
        newError[err.path] = err.message;
      });
      setErrors(newError);
      return;
    }

    // Build payload to match LandlordRentalPaymentsEditSerializer
    const editPayload = {
      description: selectedLandlordPayment.description ?? "",
      date_paid: selectedLandlordPayment.date_paid,
      amount: selectedLandlordPayment.amount,
      related_month: selectedLandlordPayment.related_month ? Number(selectedLandlordPayment.related_month) : null,
      related_landlord_rental: selectedLandlordPayment.related_landlord_rental,
    };
    // When editing to "all properties", backend requires related_landlord (or landlord_id)
    if (selectedLandlordPayment.related_landlord_rental === null) {
      editPayload.related_landlord = landlord.id;
      editPayload.landlord_id = landlord.id;
    }
    try {
      const response = await axios.post(
        `${baseURL}/admin/landlord_payments/details/${selectedLandlordPayment.id}`,
        editPayload
      );
      console.log("Payment edited successfully:", response.data);

      setOpenSnack({ open: true, vertical: "top", horizontal: "center" });
      setSeverity("success");
      setMessage("Payment edited successfully");
      setEditDialogOpen(false);
      setNewItemData();
      setAnchorEl(null);
    } catch (error) {
      console.error("Error editing payment:", error);

      setOpenSnack({ open: true, vertical: "top", horizontal: "center" });
      setSeverity("error");
      setMessage("Error editing payment");
    }
  };


  return (
    <Box m="20px">

        <FormControl sx={{ minWidth: 120,marginLeft:3}}>
            <InputLabel id="month-select-label">Month</InputLabel>
            <Select
              labelId="month-select-label"
              value={filterMonth}
              onChange={handleFilterMonthChange}
            >
              <MenuItem value="All Months">All Months</MenuItem>
              {/* Add other month options here */}
              <MenuItem value="1">January</MenuItem>
              <MenuItem value="2">February</MenuItem>
              <MenuItem value="3">March</MenuItem>
              <MenuItem value="4">April</MenuItem>
              <MenuItem value="5">May</MenuItem>
              <MenuItem value="6">June</MenuItem>
              <MenuItem value="7">July</MenuItem>
              <MenuItem value="8">August</MenuItem>
              <MenuItem value="9">September</MenuItem>
              <MenuItem value="10">October</MenuItem>
              <MenuItem value="11">November</MenuItem>
              <MenuItem value="12">December</MenuItem>
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: 120 }}>
            <InputLabel id="year-select-label">Year</InputLabel>
            <Select
              labelId="year-select-label"
              value={filterYear}
              onChange={handleFilterYearChange}
            >
              <MenuItem value="All Years">All Years</MenuItem>
              {/* Add year options dynamically or statically */}
              <MenuItem value="2026">2026</MenuItem>
              <MenuItem value="2025">2025</MenuItem>
              <MenuItem value="2024">2024</MenuItem>
              <MenuItem value="2023">2023</MenuItem>
              <MenuItem value="2022">2022</MenuItem>
            </Select>
          </FormControl>
      <Box sx={{ p: 3 }}>
        <FlexBetween>
          
          <BasicCard
            name="Expected Collection"
            value={withoutSymbolFormatter(parseFloat(landlordPayments.base_expected_collection))}
          />

          <BasicCard
            name="Percentage Deduction"
            value={withoutSymbolFormatter(parseFloat(adjustedRentalPayments))}
          />
          <BasicCard
            name="Total Amount Collected"
            value={withoutSymbolFormatter(parseFloat(landlordPayments.total_rental_payments))}
          />
          <BasicCard
            name="Amount Paid to Landlord"
            value={withoutSymbolFormatter(parseFloat(landlordPayments.total_landlord_payments))}
          />

          <BasicCard
            name="Balance"
            value={withoutSymbolFormatter(balance)} // Use the calculated balance constant
          />

          <Button
            variant="contained"
            color="primary"
            sx={{ height: 56, width: 200 }}
            onClick={handleOpenDialog}
          >
            Add Payment
          </Button>
        </FlexBetween>

        <Box sx={{ mt: 5 }}>
          <LandlordDetailsTable
            page={page}
            rowsPerPage={rowsPerPage}
            handleChangePage={handleChangePage}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
            anchorEl={anchorEl}
            handleEditDialogOpen={handleEditDialogOpen}
            handleDeleteDialogOpen={handleDeleteDialogOpen}
            handleMenuClick={handleMenuClick}
            handleMenuClose={handleMenuClose}
            landlordPayments={landlordPayments}
            isLoadingLandlordPayments={isLoadingLandlordPayments}
            errorLandlordPayments={errorLandlordPayments}
          />
        </Box>
      </Box>

      <FormDialog
        open={openDialog}
        title="Add Payment"
        text="Fill out the details below to add a new payment."
        content={
          <AddPaymentForm
            newPayment={newPayment}
            handleInputChange={handleInputChange}
            handleMonthChange={handleMonthChange}
            setMonthsAndYears={setMonthsAndYears}
            errors={errors}
          />
        }
        handleClose={handleCloseDialog}
        handleSubmit={handleAddPayment}
        isSubmittingData={isSubmittingData}
      />

      {selectedLandlordPayment ? (
        <EditLandlorPayments
          open={editDialogOpen}
          title="Edit Landlord Payment Details"
          handleClose={handleEditDialogClose}
          errors={errors}
          handleSubmit={handleEditLandlordPayments}
          handleInputChange={handleEditInputChange}
          isSubmittingData={false}
          selectedLandlordPayment={selectedLandlordPayment}
          // landlord_name={`${selectedLandlordPayment?.related_landlord_rental.related_landlord.related_user.first_name} ${selectedLandlordPayment?.related_landlord_rental.related_landlord.related_user.last_name}`}
          // description={selectedLandlordPayment?.description}
          // business_name={
          //   selectedLandlordPayment?.related_landlord_rental.related_landlord
          //     .business_name
          // }
          // rental_name={
          //   selectedLandlordPayment?.related_landlord_rental.rental_name
          // }
          // date_paid={selectedLandlordPayment?.date_paid || ""}
          // month_year={`${selectedLandlordPayment?.related_month?.month || ""} ${
          //   selectedLandlordPayment?.related_month?.year || ""
          // }`}
          // amount={selectedLandlordPayment?.amount || ""}
          // paid_by={`${selectedLandlordPayment?.added_by.first_name} ${selectedLandlordPayment?.added_by.last_name}`}
        />
      ) : null}

      <FormDialog
        open={deleteDialogOpen}
        title="Delete Payment Record"
        enableActions={true}
        content="Are you sure you want to delete this payment?"
        handleClose={handleDeleteDialogClose}
        handleSubmit={handleDeletePayment}
        buttonText="Delete Payment"
        isSubmittingData={isSubmittingData}
      />

      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={() => setOpenSnackbar(false)}
      >
        <Alert
          onClose={() => setOpenSnackbar(false)}
          severity="success"
          sx={{ width: "100%" }}
        >
          Payment added successfully!
        </Alert>
      </Snackbar>
      <TopSnack
        vertical={openSnack.vertical}
        horizontal={openSnack.horizontal}
        open={openSnack.open}
        handleCloseSnack={() => setOpenSnack({ ...openSnack, open: false })}
        message={message}
        severity={severity}
      />
    </Box>
  );
};

export default LandlordPaymentDetails;
